/**
 * shouldPlayNotificationSound.ts — decisão pura (testável) de qual som tocar.
 *
 * Spec: docs/superpowers/specs/2026-07-09-chat-notification-sound-design.md §2.
 *
 * Dado um evento SSE e o contexto do cliente, retorna qual som tocar ou `null`.
 * Sem side-effects — só regras. Facilita teste unitário exaustivo dos ramos.
 */

export type SoundKind = 'message' | 'alert_info' | 'alert_warning' | 'alert_critical'

/** Subconjunto do payload SSE `message.created` relevante para a decisão. */
export interface SoundDecisionEvent {
  type?: string
  conversation_id?: number
  message?: {
    id?: number
    kind?: string
    severity?: string | null
    author_user_id?: number | null
  }
}

export interface SoundDecisionCtx {
  /** id do usuário logado — para suprimir a própria mensagem. */
  myUserId: number | null | undefined
  /** preferência por dispositivo (localStorage). */
  soundEnabled: boolean
  /** conversa do evento está mutada. */
  isConversationMuted: boolean
}

/** Mapeia a severidade do alerta para o tier de som. */
function severityToKind(severity: string | null | undefined): SoundKind {
  switch ((severity ?? '').toLowerCase()) {
    case 'warning':
      return 'alert_warning'
    case 'critical':
    case 'fatal':
      return 'alert_critical'
    case 'debug':
    case 'info':
    default:
      // Sem severidade / severidade desconhecida → info (fallback discreto).
      return 'alert_info'
  }
}

export function shouldPlay(event: SoundDecisionEvent, ctx: SoundDecisionCtx): SoundKind | null {
  // Só eventos de nova mensagem disparam som.
  if (event.type !== 'message.created') return null

  // Preferência desligada.
  if (!ctx.soundEnabled) return null

  const msg = event.message

  // Mensagem própria — não avisar a si mesmo.
  if (msg?.author_user_id != null && msg.author_user_id === ctx.myUserId) return null

  // Conversa mutada.
  if (ctx.isConversationMuted) return null

  // Toca mesmo com a conversa aberta/focada (comportamento WhatsApp): o único
  // silêncio "por foco" não se aplica — só própria/mute/preferência silenciam.

  // Alerta = kind 'alert' OU presença de severidade (payload SSE mínimo pode
  // não trazer kind). Caso contrário, mensagem humana.
  const isAlert = msg?.kind === 'alert' || msg?.severity != null
  if (isAlert) return severityToKind(msg?.severity)

  return 'message'
}
